import { Dialog, IconButton, List, ListItem } from '@material-ui/core';
import React, { useState } from 'react';
import { ReactComponent as CloseIcon } from 'images/at_x-close.svg';
import styles from './modalDropdown.module.css';
import { Input } from '../FormInputs';
import { ReactComponent as SearchIcon } from 'images/at_search-sm-filled.svg';
import Button from '../Button';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { highlightMatch } from 'utils/utilities';
import usePaginatedOptions from 'hooks/usePaginatedOptions';
import PropTypes from 'prop-types';
import cn from 'classnames';

const ModalDropdown = ({
    open,
    setOpen,
    name,
    title,
    options = [],
    value,
    onSelect,
    onChange,
    error,
    helperText,
    placeholder,
    allowCustomValue = true,
}) => {
    const [searchValue, setSearchValue] = useState('');

    const { visibleOptions, onLoadMore, hasMore } = usePaginatedOptions({
        options,
        searchValue,
        batchSize: 20,
    });

    const { loadingRef } = useInfiniteScroll({
        loadMore: onLoadMore,
        hasMore,
    });

    const onClose = () => {
        setOpen(false);
        setSearchValue('');
    };

    const handleSelect = (option) => {
        onSelect(option);
        setOpen(false);
        setSearchValue('');
    };

    const handleChange = (e) => {
        setSearchValue(e.target.value);
    };

    const onApply = () => {
        onSelect(searchValue);
        onClose();
        onChange({ target: { name, value: searchValue } });
    };

    const handleDialogClose = (_, reason) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            // static backdrop: ignore backdrop clicks, keep Esc-to-close (bootstrap default)
            onClose={handleDialogClose}
            fullScreen
            // disablePortal renders this Dialog inside the parent UserDetails
            // react-bootstrap Modal's DOM; otherwise that modal's enforceFocus
            // steals focus from the search input (see DateInputField).
            disablePortal
            classes={{ paper: styles.modalContent }}
        >
            <div className={styles.modalHeader}>
                <div className={styles.modalHeaderContent}>
                    <span className={styles.modalTitle}>{title}</span>
                    <IconButton onClick={onClose} className={styles.modalCloseButton}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className={styles.modalSearchContainer}>
                    <Input
                        name={name}
                        placeholder={placeholder}
                        value={searchValue}
                        onChange={handleChange}
                        EndAdornmentIcon={SearchIcon}
                        error={error}
                        helperText={helperText}
                    />
                </div>
            </div>

            <div className={styles.modalBody}>
                <List className={styles.modalList}>
                    {visibleOptions.length > 0 ? (
                        visibleOptions.map((option) => (
                            <ListItem
                                button
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={cn(styles.modalListItem, {
                                    [styles.selected]:
                                        value === option.label || value === option.value,
                                })}
                            >
                                {highlightMatch(option.label, searchValue)}
                            </ListItem>
                        ))
                    ) : (
                        <ListItem className={styles.modalListItem} disabled>
                            No options found
                        </ListItem>
                    )}
                    {hasMore && <div ref={loadingRef} />}
                </List>
            </div>

            <div className={styles.modalFooter}>
                <Button variant="outline-primary" className={styles.btn} onClick={onClose}>
                    Cancel
                </Button>
                {allowCustomValue && (
                    <Button variant="primary" className={styles.btn} onClick={onApply}>
                        Add
                    </Button>
                )}
            </div>
        </Dialog>
    );
};

ModalDropdown.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.string.isRequired,
        })
    ).isRequired,
    value: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    helperText: PropTypes.string,
    placeholder: PropTypes.string,
    allowCustomValue: PropTypes.bool,
};

export default ModalDropdown;
